import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDesignStore } from '@/stores';
import { usePublishStore } from '@/stores/publishStore';
import { hasCreatorDesign } from '@/utils/hasCreatorDesign';
import i18n from '@/lib/i18n';

/** Message shown when the save before leaving fails; a key so tests and the hook agree. */
export const SAVE_BEFORE_LEAVE_FAILED_KEY = 'creator.toast.saveFailed';

/**
 * Whether leaving the creator right now would lose work.
 *
 * - An opened design (has an id) needs a save when the store marks it dirty
 *   or a save is still running: the header starts saves without waiting for
 *   them, and `saveDesign` coalesces onto the running pass, so leaving waits
 *   for it and a failure still reaches the toast below.
 * - A design that was never persisted has no id yet; it is worth saving only
 *   when the canvas holds content. A fresh entry without an object has
 *   nothing to save, even if a name change flagged the store as dirty.
 */
function needsSaveBeforeLeave(): boolean {
  const { designId, hasUnsavedChanges, isSaving } = useDesignStore.getState();
  return designId ? hasUnsavedChanges || isSaving : hasCreatorDesign();
}

/**
 * Save-then-navigate for controls that leave the creator (wordmark, "Zurück").
 *
 * Unlike the step change in the header, this never navigates after a failed
 * save: the seller stays in the creator and sees a toast, so no work is lost.
 * `saveDesign` reports failure by resolving to `null` (it stores the error in
 * `syncError`); a rejection is handled the same way in case a caller throws.
 *
 * During a publish the request is refused outright rather than queued: the
 * publish owns the save sequence and ends in a navigation of its own (the
 * overview or the cloud redirect), so a second navigation waiting behind it
 * would only race that one. The step bar shows why nothing happens.
 */
export function useSaveBeforeLeave() {
  const navigate = useNavigate();
  const [isLeaving, setIsLeaving] = useState(false);

  const saveBeforeLeave = useCallback(
    // `target` is a path, or a function that leaves on its own (a switch of
    // the creator entry has to reset stores before it navigates).
    async (target: string | (() => void)) => {
      if (isLeaving) return;
      if (usePublishStore.getState().isPublishing) return;
      const leave = () =>
        typeof target === 'function' ? target() : navigate(target);

      if (!needsSaveBeforeLeave()) {
        leave();
        return;
      }

      setIsLeaving(true);
      try {
        const saved = await useDesignStore.getState().saveDesign();
        if (!saved) {
          toast.error(i18n.t(SAVE_BEFORE_LEAVE_FAILED_KEY));
          return;
        }
        leave();
      } catch (error) {
        console.error('Save before leaving the creator failed:', error);
        toast.error(i18n.t(SAVE_BEFORE_LEAVE_FAILED_KEY));
      } finally {
        setIsLeaving(false);
      }
    },
    [isLeaving, navigate]
  );

  return { saveBeforeLeave, isLeaving };
}
